import React from 'react';
import { SpeakerWaveIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage5 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 5: Launch it Well" icon={<SpeakerWaveIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Follow the 6-step launch process: Draft → Share with Managers → Iterate → Train Managers → Announce → First Cycle</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Get manager buy-in and comfort with the framework before org-wide rollout</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Create a performance handbook that covers why, what, when, and how</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Execute a multi-channel communication strategy for maximum adoption</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">You've Built the Thing. Let's Launch It Right!</h3>
        <p className="text-gray-300 mb-6">
          Building a great framework is one thing, launching it well and getting folks using it as intended is another challenge entirely. 
          We're going to take you through our own battle-tested approach to launching performance frameworks well.
        </p>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <p className="text-gray-300 text-sm">
            <strong className="text-white">FYI:</strong> This approach can be broadly applied to other people products too.
          </p>
        </div>

        <h4 className="text-lg font-semibold text-white mb-4">Our 6-Step Launch Process:</h4>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">1.</span>
              <h5 className="text-primary-light font-semibold">Draft V1 Documentation</h5>
            </div>
            <p className="text-gray-300 text-sm">Create performance handbook for managers</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">2.</span>
              <h5 className="text-primary-light font-semibold">Share & Invite Feedback</h5>
            </div>
            <p className="text-gray-300 text-sm">Get manager input async or in workshops</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">3.</span>
              <h5 className="text-primary-light font-semibold">Iterate & Publish</h5>
            </div>
            <p className="text-gray-300 text-sm">Update based on feedback and share final version</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">4.</span>
              <h5 className="text-primary-light font-semibold">Get Managers Comfy</h5>
            </div>
            <p className="text-gray-300 text-sm">1:1s, test-runs, and group sessions</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">5.</span>
              <h5 className="text-primary-light font-semibold">Org-Wide Rollout</h5>
            </div>
            <p className="text-gray-300 text-sm">Multi-channel announcement strategy</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">6.</span>
              <h5 className="text-primary-light font-semibold">Run First Cycle</h5>
            </div>
            <p className="text-gray-300 text-sm">Execute end-to-end with support</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm italic">
          Note: the below assumes you've already handled some comms to the wider business about the work you are doing, 
          but there's a comms template you can use if needed.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">1.</span>
          <h3 className="text-2xl font-semibold text-white">Draft V1 Documentation (Performance Handbook)</h3>
        </div>
        <p className="text-gray-300 mb-6">
          You need a performance handbook that covers all the previous work you've done in previous steps including:
        </p>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Why performance is important here</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">What it means to us & how we use it here</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">What, when & how we assess, as well as who is assessed</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Additional guidance around things like 1:1s and underperformance</span>
          </li>
        </ul>

        <PlaybookImage 
          src="https://auth.openorg.fyi/storage/v1/object/public/resources/performance/performance-handbook-template.png"
          alt="Screenshot from our Performance Handbook template on Notion"
          className="mb-4 rounded-lg border border-gray-700"
        />
        <p className="text-gray-400 text-sm text-center mb-6">Screenshot from our Performance Handbook template on Notion</p>

        <p className="text-gray-300 mb-6">
          You can also use our performance handbook template on Notion as a comprehensive guide.
        </p>
        
        <ToolCallout
          tool="notion"
          label="Performance Handbook Template"
          href="https://www.notion.so/Open-Org-s-Performance-Playbook-Template-1b3f015e5d468046bdceffe4cf92200c?pvs=21"
          description="Complete template with all sections, forms, and guidance for building your performance framework"
        />
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">2.</span>
          <h3 className="text-2xl font-semibold text-white">Share & Invite Feedback from Managers</h3>
        </div>
        <p className="text-gray-300 mb-6">
          You have two options here depending on your size & set up:
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Async Approach</h4>
            <p className="text-gray-300 text-sm mb-3">
              Share the V1 Playbook + a Notion 1-pager with clear context, asks & space for folks to provide feedback 
              (or comment directly in your V1 handbook).
            </p>
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Perfect if:</strong> You're remote, or have a preference for async working. 
              We also find this approach slightly more inclusive when it comes to encouraging equal voice.
            </p>
          </div>
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Sync Approach</h4>
            <p className="text-gray-300 text-sm mb-3">
              We'd still encourage you to share something upfront to make the most of your time but you might prefer to 
              get folks together to run some workshops & gather feedback in-person.
            </p>
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Perfect if:</strong> You're in-office or hybrid and lean more towards in-person 
              interaction & collaboration. Aim for groups of 5-8 max, with good representation from across the business.
            </p>
          </div>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">Goal:</h4>
          <p className="text-gray-300 text-sm">
            Regardless of which approach you choose, your goal here is to validate as best as possible the V1 you have built, 
            but also identify gaps, friction points, or elevation opportunities that you may have missed when designing it.
          </p>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">3.</span>
          <h3 className="text-2xl font-semibold text-white">Iterate and Publish the Latest Version</h3>
        </div>
        <p className="text-gray-300 mb-6">
          A few things to think about here:
        </p>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              It's likely you haven't been able to action every piece of feedback you were given. Be sure to give them as much visibility & context as possible on the things you've taken forward (& why) and the things you haven't (& why). <strong className="text-white">Nothing kills co-creation efforts quicker than folks feeling unheard.</strong>
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              Stress that this is never going to be a final version. This is a living framework that will need to be reviewed & iterated on long-term.
            </span>
          </li>
        </ul>

        <CopyToNotion title="Feedback Summary Template" slack={true}>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">Performance Framework Feedback Summary</h4>
              <p className="text-gray-300 text-sm mb-3">Thanks everyone for your input on our performance framework V1! Here's what we heard and what we're doing about it:</p>
              
              <div className="space-y-3">
                <div>
                  <h5 className="font-medium text-white mb-1">Changes We're Making:</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <ul className="space-y-1 text-gray-300 text-sm">
                      <li>• [Feedback point] → [Action taken]</li>
                      <li>• [Feedback point] → [Action taken]</li>
                    </ul>
                  </div>
                </div>

                <div>
                                      <h5 className="font-medium text-white mb-1">Parking for Future Iterations:</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <ul className="space-y-1 text-gray-300 text-sm">
                      <li>• [Feedback point] → [Why we're parking this]</li>
                      <li>• [Feedback point] → [Why we're parking this]</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-gray-300 text-sm mt-3">
                <strong>Remember:</strong> This framework will continue to evolve. We'll review and iterate after our first few cycles based on how it works in practice.
              </p>
            </div>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">4.</span>
          <h3 className="text-2xl font-semibold text-white">Get Managers Comfy</h3>
        </div>
        <p className="text-gray-300 mb-6">
          Before you roll this out, you want to make sure your managers know this framework inside out. Our view is that whilst you will likely lead on communicating the launch of this to the wider business, it's managers themselves who need to get into the weeds of this with their teams.
        </p>

        <h4 className="text-lg font-semibold text-white mb-4">We'd advise considering a blend of the following:</h4>
        <div className="space-y-4 mb-6">
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <h5 className="text-primary-light font-semibold mb-2">1:1s</h5>
            <p className="text-gray-300 text-sm">Individual run-throughs with every manager to make sure everyone is comfortable</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <h5 className="text-primary-light font-semibold mb-2">Test Runs</h5>
            <p className="text-gray-300 text-sm">Pilot it, together. Take them all through a dry run of a cycle</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <h5 className="text-primary-light font-semibold mb-2">Group Sessions</h5>
            <p className="text-gray-300 text-sm">Get managers together, share reflections, questions together</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm">
          This is your chance to make sure they can guide their teams through the framework effectively.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">5.</span>
          <h3 className="text-2xl font-semibold text-white">Org-Wide Announcement & Roll Out</h3>
        </div>
        
        <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700 mb-6">
          <h4 className="text-white font-semibold mb-4">Pre-Launch Checklist:</h4>
          <div className="space-y-3">
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
              <span className="text-gray-300 text-sm">Do folks know this is coming?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
              <span className="text-gray-300 text-sm">Have you shared this with Managers?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
              <span className="text-gray-300 text-sm">Have you iterated on it with Managers?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
              <span className="text-gray-300 text-sm">Have you soft-tested it with Managers?</span>
            </div>
            <div className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
              <span className="text-gray-300 text-sm">Have you documented everything you can? (e.g. Handbook)</span>
            </div>
          </div>
        </div>

        <h4 className="text-lg font-semibold text-white mb-4">Hard Learned Lessons:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span className="text-gray-300">
              <strong className="text-white">Get the timing right.</strong> Don't try to launch this in OKR week. Or end of financial year when everyone's trying to hit targets. You need to pick a moment which gives you the best possible chance of grabbing people's attention.
            </span>
          </li>
          <li className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span className="text-gray-300">
              <strong className="text-white">Don't overthink or over-lean on some big bang announcement.</strong> The reality is, you'll need to communicate this multiple times, through multiple channels in order to land the message home.
            </span>
          </li>
          <li className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span className="text-gray-300">
              <strong className="text-white">Remember to hit on the key points:</strong> What are we doing, Why are we doing it, What's changed (+ why), Who's been involved (+ why), When is this happening?, What do 'I' need to do next?
            </span>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-4">Suggested Communication Strategy:</h4>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">Town Hall Announcement</h5>
            <p className="text-gray-300 text-sm">Main announcement with Q&A</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">Post-Share</h5>
            <p className="text-gray-300 text-sm">Notion Doc + Loom Video</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">Slack Follow Up</h5>
            <p className="text-gray-300 text-sm">TL;DR + Video link</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">Manager 1:1s</h5>
            <p className="text-gray-300 text-sm">Individual discussions</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">AMA Session</h5>
            <p className="text-gray-300 text-sm">Following Town Hall</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h5 className="text-white font-semibold mb-2">Ongoing 1:1s</h5>
            <p className="text-gray-300 text-sm">Build familiarity before first cycle</p>
          </div>
        </div>

        <CopyToNotion title="Slack Announcement Template" slack={true}>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">New Performance Framework Launch</h4>
              <div className="bg-gray-800 p-4 rounded border border-gray-700">
                <p className="text-gray-300 text-sm mb-3">
                  Hey team! 👋 We're excited to share our new performance framework that we've been working on with your manager input over the past [timeframe].
                </p>
                
                <p className="text-gray-300 text-sm mb-3">
                  <strong className="text-white">What:</strong> A clear, lightweight approach to performance conversations focused on Results, Execution, and Behaviours<br/>
                  <strong className="text-white">Why:</strong> [Your performance philosophy from Stage 1]<br/>
                  <strong className="text-white">When:</strong> [Your timing/frequency]<br/>
                  <strong className="text-white">Who:</strong> Everyone (yes, including leadership!)
                </p>

                <p className="text-gray-300 text-sm mb-3">
                  📖 <strong>Full details:</strong> [Link to your performance handbook]<br/>
                  🎥 <strong>Walkthrough video:</strong> [Link to Loom]<br/>
                  <strong>Questions?</strong> We'll have an AMA session next [day] or ask in your next 1:1
                </p>

                <p className="text-gray-300 text-sm">
                  This framework was co-created with manager input and feedback. Thanks to everyone who contributed! 🙌
                </p>
              </div>
            </div>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">6.</span>
          <h3 className="text-2xl font-semibold text-white">First Cycle</h3>
        </div>
        <p className="text-gray-300 mb-6">
          Depending on how close your first cycle is to your roll-out announcement, you may or may not want to take the opportunity 
          to remind folks a little about the what + the why, and perhaps run a refresher or check-in with managers, and them with their reports too.
        </p>

        <p className="text-gray-300 mb-6">
          Hopefully you have everything you needed grounded & well-documented for folks to refer back to. 
          <strong className="text-white"> So now's the time to do the thing.</strong>
        </p>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">First Cycle Support</h4>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li>• Be available for manager questions and support</li>
            <li>• Monitor progress and identify any early friction points</li>
            <li>• Gather feedback for future iterations</li>
            <li>• Celebrate completion and early wins</li>
          </ul>
        </div>

        <p className="text-gray-300 mb-4">Good luck. We'll see you on the other side to talk about how we embed & manage this long term.</p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Run your first performance cycle end-to-end</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Successfully launched with manager buy-in and clear communication</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Comprehensive performance handbook for ongoing reference</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Initial feedback and learnings for continuous improvement</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Next:</strong> Move to <Link to="/playbooks/how-to-build-launch-embed-performance/stage-6" className="text-primary hover:text-primary-light">Stage 6: Embed it, and Manage it like a Product </Link> 
               to make performance practices sticky and continuously improve the framework.
            </p>
          </div>
        </div>
      </PlaybookCard>

      <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">
            This is a new process, lightly-tested probably. Things will most likely not go perfectly. Issues will arise, 
            and people will most likely find problems with it. Don't panic. It doesn't mean it's not right.
          </p>
          <p className="text-gray-300 mb-3">
            You can head this off in a couple of ways:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Communicate that exact message up front; this is new, will need tweaking and may take 1-2 cycles to perfect</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">This will continue to evolve over time too. It's not static</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">We welcome ideas and feedback after cycles to help us with that work</span>
            </li>
          </ul>
        </div>
      </ExpandableCallout>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage5; 